import Analytics from '@/utils/analytics';
import { EmployeeRole } from '@/types';
import { Fennel } from '@/analytics/fennel/fennel';
import { partnerSupplier } from '@/utils/environment';
import { useAuth } from '@/contexts/auth-context';
import { useEffect } from 'react';
import { useSalesPortalEmployeeRole } from '@/experiences/sales-portal/blocs/hooks/use-sales-portal-employee-role';

interface UseSalesPortalFennelInitParams {
  restaurantUUID: string;
  supplierUUID: string;
}

/**
 * Initializes the Fennel analytics tracker for the Sales Portal experience.
 *
 * Sales Portal is rendered outside UserContextProvider, so Fennel cannot be
 * initialized there. This hook provides the equivalent initialization using
 * AuthContext and a dedicated employee-role query.
 *
 * Re-initializes whenever restaurantUUID, supplierUUID, or user identity changes.
 */
export const useSalesPortalFennelInit = ({
  restaurantUUID,
  supplierUUID,
}: UseSalesPortalFennelInitParams): void => {
  const authenticatedUserUUID = useAuth((s) => s.authenticatedUserUUID);
  const { employeeRole, loading: isEmployeeRoleLoading } =
    useSalesPortalEmployeeRole(supplierUUID);

  useEffect(() => {
    if (isEmployeeRoleLoading) {
      return;
    }
    Fennel.getInstance().initialize(
      {
        businessOrganizationUUID: partnerSupplier.businessOrganizationUUID,
        employeeUUID: authenticatedUserUUID ?? undefined,
        restaurantUUID,
        supplierEmployeeRole: employeeRole ?? undefined,
        supplierUUID,
        userRole: employeeRole ?? EmployeeRole.User,
      },
      Analytics,
    );
  }, [
    authenticatedUserUUID,
    employeeRole,
    isEmployeeRoleLoading,
    restaurantUUID,
    supplierUUID,
  ]);
};
